import { useState, useEffect } from 'react';

/**
 * Viewport breakpoint state that is safe to hydrate against.
 *
 * <p>The obvious version of this — `useState(window.innerWidth < 768)` — is
 * what four components here used to do, and it is incompatible with
 * prerendering. The static HTML is generated at one fixed viewport, so a
 * visitor whose window falls on the other side of the breakpoint produces a
 * different first render than the markup being hydrated. React notices the
 * mismatch, throws away that subtree and re-renders it from scratch, which
 * showed up as "Minified React error #418" on every route and quietly undid
 * part of what the prerender was for.
 *
 * <p>The fix is that the first render must not depend on the viewport at all.
 * Both sides start from `initial`, hydration matches by construction, and the
 * real measurement is taken in an effect immediately after mount. Choosing
 * `true` as the default means the prerendered HTML is the mobile layout, which
 * is the right way round for two reasons: Google indexes mobile-first, and most
 * of this site's visitors are on phones — so the layout correction after mount
 * is invisible to the majority and only desktop sees a single frame of the
 * narrow layout.
 *
 * <p>scripts/prerender.js sets a mobile viewport to match this default. The two
 * have to agree; if you change one, change the other.
 */
export const PRERENDER_ASSUMES_MOBILE = true;

const useIsMobile = (breakpoint = 768, initial = PRERENDER_ASSUMES_MOBILE) => {
    const [isMobile, setIsMobile] = useState(initial);

    useEffect(() => {
        const measure = () => setIsMobile(window.innerWidth < breakpoint);
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [breakpoint]);

    return isMobile;
};

export default useIsMobile;
